import React, { useState, useEffect } from "react";
import axios from "axios";
import { Box, Typography, Card, CardContent, Alert } from "@mui/material";

function NitrogenBalanceDisplay({
  cropType,
  currentAmount,
  refreshTrigger,
  starterAmountOverride,
  seasonCapOverride,
}) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const teamName = localStorage.getItem("username");
  const token = localStorage.getItem("token");

  const fetchBalance = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/getNitrogenBalance", {
        params: { teamName, cropType: cropType || "corn" },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        setBalance(response.data);
      }
    } catch (error) {
      console.error("Error fetching nitrogen balance:", error);
      setError("Failed to load nitrogen balance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [cropType, refreshTrigger]);

  if (loading) {
    return (
      <Card sx={{ mb: 2, backgroundColor: "#f5f5f5" }}>
        <CardContent>
          <Typography variant="h6">Loading Nitrogen Balance...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!balance) {
    return null;
  }

  const usedAmount = parseFloat(balance.used) || 0;
  const remainingAmount = parseFloat(balance.remaining) || 0;
  const totalAllowedAmount = parseFloat(balance.totalAllowed) || 0;
  const isLow = remainingAmount < 60; // Less than 60 lbs/ac remaining
  const isCritical = remainingAmount < 30; // Less than 30 lbs/ac remaining

  // Calculate future balance if amount is entered
  const amountValue = parseFloat(currentAmount) || 0;
  const futureRemaining = remainingAmount - amountValue;
  const showFutureBalance = amountValue > 0 && amountValue >= 30;

  const starterFertilizer = Number.isFinite(parseFloat(starterAmountOverride))
    ? parseFloat(starterAmountOverride)
    : cropType === "cotton"
      ? 0
      : 30;

  const maxScale =
    Number.isFinite(parseFloat(seasonCapOverride)) &&
    parseFloat(seasonCapOverride) > 0
      ? parseFloat(seasonCapOverride)
      : cropType === "cotton"
        ? 140
        : 270;

  const currentUsedWithStarter = usedAmount + starterFertilizer;
  const futureUsedWithStarter = currentUsedWithStarter + amountValue;
  const percentageUsedOnScale = clampPercent(
    (currentUsedWithStarter / maxScale) * 100,
  );
  const percentageFutureUsedOnScale = clampPercent(
    (futureUsedWithStarter / maxScale) * 100,
  );
  const percentageAdditionOnScale = clampPercent(
    (amountValue / maxScale) * 100,
  );
  const percentageStarterOnScale = clampPercent(
    (starterFertilizer / maxScale) * 100,
  );
  const percentageUsedWithoutStarter = clampPercent(
    (usedAmount / maxScale) * 100,
  );

  const defaultTicks =
    cropType === "cotton"
      ? [0, 30, 60, 90, 120, 140]
      : [0, 30, 60, 90, 120, 150, 180, 210, 240, 270];
  const tickValues = Array.from(
    new Set(
      [...defaultTicks, roundToWhole(starterFertilizer)]
        .filter((v) => Number.isFinite(v) && v >= 0 && v <= maxScale)
        .map((v) => roundToWhole(v)),
    ),
  ).sort((a, b) => a - b);

  return (
    <Card
      sx={{
        mb: 2,
        mt: 5,
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ padding: "20px !important" }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: 600, mb: 2, color: "#212121" }}
        >
          Nitrogen Usage
        </Typography>

        <Box sx={{ mb: 3 }}>
          {/* Key metrics in a clean row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#757575", display: "block", mb: 0.5 }}
              >
                Total Allowed
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#212121" }}
              >
                {totalAllowedAmount.toFixed(0)} lbs/ac
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography
                variant="caption"
                sx={{ color: "#757575", display: "block", mb: 0.5 }}
              >
                Remaining
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: isCritical ? "#f44336" : isLow ? "#ff9800" : "#4caf50",
                }}
              >
                {showFutureBalance ? (
                  <span
                    style={{
                      color: futureRemaining < 0 ? "#f44336" : "#2196f3",
                    }}
                  >
                    {remainingAmount.toFixed(0)} - {amountValue.toFixed(0)} ={" "}
                    {futureRemaining.toFixed(0)} lbs/ac
                  </span>
                ) : (
                  `${remainingAmount.toFixed(0)} lbs/ac`
                )}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" sx={{ color: "#757575", mb: 1.5 }}>
            Used:{" "}
            <strong style={{ color: "#212121" }}>
              {parseFloat(currentUsedWithStarter).toFixed(0)} lbs/ac
            </strong>{" "}
            (includes {starterFertilizer.toFixed(0)} lbs/ac starter)
          </Typography>

          {/* Progress Bar Section */}
          <Box sx={{ position: "relative", mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                position: "relative",
                mb: 0.5,
              }}
            >
              {tickValues.map((tick) => {
                if (tick === 0) {
                  return (
                    <Typography
                      key={`tick-${tick}`}
                      variant="caption"
                      sx={{ fontSize: "0.7rem", color: "#757575" }}
                    >
                      {tick}
                    </Typography>
                  );
                }

                if (tick === maxScale) {
                  return (
                    <Typography
                      key={`tick-${tick}`}
                      variant="caption"
                      sx={{ fontSize: "0.7rem", color: "#757575" }}
                    >
                      {tick}
                    </Typography>
                  );
                }

                return (
                  <Typography
                    key={`tick-${tick}`}
                    variant="caption"
                    sx={{
                      fontSize: "0.7rem",
                      color: "#757575",
                      position: "absolute",
                      left: `${(tick / maxScale) * 100}%`,
                      transform: "translateX(-50%)",
                    }}
                  >
                    {tick}
                  </Typography>
                );
              })}
              {showFutureBalance && amountValue > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.7rem",
                    color: futureRemaining < 0 ? "#f44336" : "#2196f3",
                    fontWeight: 700,
                    position: "absolute",
                    left: `${percentageFutureUsedOnScale}%`,
                    transform: "translateX(-50%)",
                  }}
                >
                  {futureUsedWithStarter.toFixed(0)}
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              position: "relative",
              height: 24,
              borderRadius: 3,
              backgroundColor: "#f5f5f5",
              overflow: "visible",
            }}
          >
            {/* Starter fertilizer bar */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                height: "100%",
                width: `${percentageStarterOnScale}%`,
                backgroundColor: "#ffd54f",
                borderRadius: "3px 0 0 3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "width 0.3s ease-in-out",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#000" }}
              >
                Starter
              </Typography>
            </Box>
            {/* Used balance bar */}
            <Box
              sx={{
                position: "absolute",
                left: `${percentageStarterOnScale}%`,
                top: 0,
                height: "100%",
                width: `${percentageUsedWithoutStarter}%`,
                backgroundColor: isCritical
                  ? "#f44336"
                  : isLow
                    ? "#ff9800"
                    : "#4caf50",
                borderRadius:
                  !showFutureBalance || amountValue === 0 ? "0 3px 3px 0" : "0",
                transition: "width 0.3s ease-in-out",
              }}
            />
            {/* Future addition overlay */}
            {showFutureBalance && amountValue > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  left: `${percentageUsedOnScale}%`,
                  top: 0,
                  height: "100%",
                  width: `${percentageAdditionOnScale}%`,
                  backgroundColor: futureRemaining < 0 ? "#f44336" : "#2196f3",
                  borderRadius: "0 3px 3px 0",
                  opacity: 0.8,
                  transition: "all 0.3s ease-in-out",
                }}
              />
            )}
          </Box>
        </Box>

        {/* Preview Box */}
        {showFutureBalance && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              backgroundColor: futureRemaining < 0 ? "#ffebee" : "#e3f2fd",
              borderRadius: 1.5,
              borderLeft:
                futureRemaining < 0 ? "4px solid #f44336" : "4px solid #2196f3",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: futureRemaining < 0 ? "#c62828" : "#1565c0",
                mb: 0.5,
              }}
            >
              Preview: {parseFloat(currentUsedWithStarter).toFixed(0)} +{" "}
              {amountValue.toFixed(0)} = {futureUsedWithStarter.toFixed(0)}{" "}
              lbs/ac
            </Typography>
            <Typography variant="caption" sx={{ color: "#757575" }}>
              {futureRemaining < 0
                ? "⚠️ Exceeds allowed limit"
                : `${futureRemaining.toFixed(0)} lbs/ac will remain`}
            </Typography>
          </Box>
        )}

        {/* Status Alerts - Only show when critical or low */}
        {isCritical && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {remainingAmount === 0
                ? "Season limit for nitrogen reached"
                : "Critical: Less than 30 lbs/ac remaining"}
            </Typography>
          </Alert>
        )}

        {isLow && !isCritical && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Warning: Less than 60 lbs/ac remaining
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

const roundToWhole = (value) => Math.round(Number(value));
const clampPercent = (value) => Math.max(0, Math.min(100, value));

export default NitrogenBalanceDisplay;
